import { useAppDispatch, useAppSelector } from '@/app/hooks';
import AccountGate from '@/components/AccountGate';
import AppUpdatePrompt from '@/components/AppUpdatePrompt';
import AuthCallback from '@/components/AuthCallback';
import Billing from '@/components/Billing';
import ContactSupportBilling from '@/components/ContactSupportBilling';
import DrawingDrawer from '@/components/DrawingNav/DrawingDrawer';
import DrawingHeader from '@/components/DrawingNav/DrawingHeader';
import { drawingToolSelected } from '@/components/DrawingNav/drawingSlice';
import LoginPage from '@/components/LoginPage';
import Menu from '@/components/Menu';
import { navigationMenuLayout, setNavigationMenuLayout } from '@/components/Menu/menuSlice';
import PQMap from '@/components/PQMap';
import RequireAuth from '@/components/RequireAuth';
import { SavePage } from '@/components/SavePage';
import TopNav from '@/components/TopNav';
import { usePwaInstall } from '@/hooks/usePwaInstall';
import useScreenOrientation from '@/hooks/useScreenOrientation';
import { db } from '@/lib/db';
import { getDeviceType } from '@/lib/device-utils';
import { SHAPE_LABELS } from '@/lib/saved-items';
import { Modal, Stack, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { Notifications } from '@mantine/notifications';
import { useLiveQuery } from 'dexie-react-hooks';
import { useEffect, useRef } from 'react';
import { Route, Routes } from 'react-router';
import './App.css';

function App() {
  // Capture install events and register updates while AuthKit restores a session,
  // including on a first visit before the user has signed in.
  const install = usePwaInstall();

  return (
    <>
      <Notifications position='top-center' />
      <AppUpdatePrompt />
      <RequireAuth fallback={<LoginPage install={install} />}>
        <AccountGate><AuthenticatedApp /></AccountGate>
      </RequireAuth>
    </>
  );
}

function AuthenticatedApp() {
  const dispatch = useAppDispatch();
  const currentNavigationMenuLayout = useAppSelector(navigationMenuLayout);
  const toolLabel = SHAPE_LABELS[useAppSelector(drawingToolSelected)];
  const screenOrientation = useScreenOrientation();
  const deviceType = getDeviceType();
  const [orientationModalOpen, orientationModalHandler] = useDisclosure(false)
  const locationRequested = useRef(false);
  const allowLocation = useLiveQuery(() => db.userSettings.where('name').equals('allowLocation').first());

  useEffect(() => {
    // undefined is still loading; a null value means no preference has been recorded.
    if (!allowLocation || allowLocation.value !== null || locationRequested.current) return;
    locationRequested.current = true;
    let cancelled = false;
    const savePermission = (value: boolean) => {
      if (!cancelled) void db.userSettings.update(allowLocation.id, { value }).catch(() => {
        // A failed preference write must not break startup or trigger a redirect.
      });
    };
    try {
      if (!navigator.geolocation) savePermission(false);
      else navigator.geolocation.getCurrentPosition(() => savePermission(true), () => savePermission(false), {
        enableHighAccuracy: true, timeout: 10_000, maximumAge: 0,
      });
    } catch { savePermission(false); }
    return () => { cancelled = true; };
  }, [allowLocation])

  useEffect(() => {
    if (screenOrientation.includes('landscape') && deviceType !== 'desktop') {
      orientationModalHandler.open();
    } else {
      orientationModalHandler.close();
    }
  }, [screenOrientation, deviceType, orientationModalHandler])

  return (
    <>
      <Routes>
        <Route path='/callback' element={<AuthCallback />} />
        <Route path='/billing' element={<Billing />} />
        <Route path='/support' element={<ContactSupportBilling />} />
        <Route path='*' element={
          <>
            <header style={{ zIndex: 100, height: 60 }}>
              {currentNavigationMenuLayout === 'default' && <TopNav />}
              {currentNavigationMenuLayout === 'drawing' && <DrawingHeader title={`Create ${toolLabel}`} onClose={() => dispatch(setNavigationMenuLayout('default'))} />}
            </header>
            <main style={{ height: 'calc(100% - 65px - 60px)' }}>
              <PQMap />
              <SavePage />
            </main>
            <footer style={{ zIndex: 100, height: 65 }}>
              {currentNavigationMenuLayout === 'default' && <Menu />}
              {currentNavigationMenuLayout === 'drawing' && <DrawingDrawer title={`${toolLabel} Tool`} />}
            </footer>
          </>
        } />
      </Routes>
      <Modal opened={orientationModalOpen} onClose={orientationModalHandler.toggle} trapFocus={false} autoFocus={false} withCloseButton={false} title='Orientation Warning' size='md' centered>
        <Stack gap='md'>
          <Text>ParcelQuest Online is best viewed in Portrait mode.</Text>
          <Text>Please rotate your device to continue.</Text>
        </Stack>
      </Modal>
    </>
  )
}

export default App
